/**
 * The model picker: a popover that unfolds from the composer's footer.
 *
 * Upward, as in the reference, whenever there is room for it there (which is
 * every in-session composer, since those sit on the bottom edge of their
 * column). The product home's composer sits a third of the way down the page,
 * where opening upward would put half the list above the top of the window,
 * so there it opens downward instead. Either way the card is absolutely
 * placed, so opening it moves nothing.
 *
 * Round 7, owner: "I want Conductor style for model picker. Right now the
 * dropdown picker looks ugly." It was a native `<select>` whose options read
 * "claude-opus-4-8 ($5/$25 per M tokens)". What replaces it is the shape
 * Conductor uses: a quiet raised card, rows grouped under a hairline-separated
 * heading, the current row filled and ticked, and a trigger in the footer that
 * says nothing but the model's name. Round 9, owner: the visible number
 * column is gone ("let's remove numbering from model picker") — the 1-9 keys
 * still pick, silently, for whoever knows them.
 *
 * What was adapted rather than copied, and why:
 *  - the reference groups by PROVIDER. We have one provider, so the honest
 *    analogue is the model FAMILY (labels.modelGroups), which is the real
 *    thing behind both the capability step and the price. The price then gets
 *    said once per family instead of eight times.
 *  - the reference puts a provider mark on each heading. One provider means
 *    one mark, so it sits on the trigger (where the reference also has it)
 *    and the headings carry words only. A different glyph per family would be
 *    an invention.
 *  - no favourite stars and no NEW badge: nothing in the client's model list
 *    carries a release date, and the client-visible account object
 *    (MeResponse/PatchMeRequest) has no field a favourite could live in. Both
 *    would have to be faked, so neither is here.
 *
 * Behaviour is exactly what the select did: null means "follow the default"
 * (product, then account, then the server), any other value is an explicit
 * override for this conversation.
 *
 * The rows themselves live in modelRows.tsx, shared with the settings pages'
 * ModelTierSelect (2026-09-02), so a tier looks the same wherever it is chosen.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import type { Effort } from '../../api/types';
import { modelDisplayName, modelGroups } from './labels';
import { AllModelsToggle, FamilyGroup, ModelRow, TierRows } from './modelRows';

/** Tall enough for the whole list; the room on screen may cut it shorter. */
const POPOVER_MAX_HEIGHT = 560;
/** The air between the trigger and the card, and between the card and the edge. */
const POPOVER_GAP = 8;
const VIEWPORT_MARGIN = 12;

/** The mark beside the model name: one provider, one glyph. */
function ModelGlyph() {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      <path d="M12 3.5 13.9 9l5.6 2-5.6 2-1.9 5.5L10.1 13 4.5 11l5.6-2z" />
    </svg>
  );
}

/** One pickable row: `model` null is the "follow the default" row. */
interface PickerRow {
  /** position in the flat list, which is also the number that picks it */
  index: number;
  model: string | null;
  label: string;
  /** the quiet right-hand line (the default row names what it resolves to) */
  meta: string | null;
}

export function ModelPicker({
  model,
  defaultModel,
  onChange,
  onTier,
  disabled = false,
  disabledReason,
  testId,
}: {
  /** the chosen override, or null for "inherit the default" */
  model: string | null;
  /** what "Default" resolves to right now */
  defaultModel: string;
  onChange: (model: string | null) => void;
  /**
   * A tier row picked: the tier's model AND effort together (a tier is a
   * setting, not just a model). When absent, a tier row sets the model only.
   */
  onTier?: (model: string, effort: Effort) => void;
  disabled?: boolean;
  /** what to say instead of the usual tooltip while disabled */
  disabledReason?: string;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  // The full family list is one disclosure away (round-9 follow-up; owner:
  // "should we remove models and just keep those 3?"): the tiers are the
  // way in, the list is there for whoever wants a specific id. It opens by
  // itself when the conversation is on a model the tiers do not cover, so
  // the ticked row (and its keyboard focus) is never hidden. Only Default
  // keeps the list folded: an explicit override, tier or not, opens it.
  const [showAll, setShowAll] = useState(false);
  const listOpen = showAll || model !== null;
  // Which way the card unfolds, and how tall it may be. Measured, because a
  // list that opens upward out of the top of the window has rows nobody can
  // reach: the session composer sits at the bottom of its column and has all
  // the room above it, the product home's sits a third of the way down and
  // has it below.
  const [placement, setPlacement] = useState({ up: true, maxHeight: POPOVER_MAX_HEIGHT });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const groups = useMemo(() => modelGroups(model), [model]);
  /** The flat order: Default first, then every model. Index + 1 is its key. */
  const order = useMemo<Array<string | null>>(
    () => [null, ...groups.flatMap((g) => [...g.models])],
    [groups],
  );
  const selectedIndex = Math.max(0, order.indexOf(model));
  const currentName = modelDisplayName(model ?? defaultModel);

  // A click anywhere else closes, and deliberately does NOT move focus: the
  // pointer already went where the reader meant it to go.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // A turn starting while the list is open shuts it: the rows would still be
  // clickable behind a trigger that has just gone dead (the server answers
  // 409 generation_active to a session write mid-generation). Done in render
  // so the open list is never committed against a dead trigger.
  if (disabled && open) setOpen(false);

  // Measured as the list opens, before anything renders, so the card never
  // appears in one place and then hops to another.
  const openList = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect !== undefined) {
      const above = rect.top - POPOVER_GAP - VIEWPORT_MARGIN;
      const below = window.innerHeight - rect.bottom - POPOVER_GAP - VIEWPORT_MARGIN;
      const up = above >= POPOVER_MAX_HEIGHT || above >= below;
      setPlacement({
        up,
        maxHeight: Math.max(200, Math.min(POPOVER_MAX_HEIGHT, up ? above : below)),
      });
    }
    setOpen(true);
  };

  // Opening lands on the row you are already on, so the first arrow key moves
  // from where you are rather than from the top of the list.
  useEffect(() => {
    if (!open) return;
    itemsRef.current[selectedIndex]?.focus();
  }, [open, selectedIndex]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (next: string | null) => {
    if (next !== model) onChange(next);
    close();
  };

  const focusAt = (index: number) => {
    const wrapped = ((index % order.length) + order.length) % order.length;
    itemsRef.current[wrapped]?.focus();
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      // Tab out of a menu closes it, and hands focus back to the trigger so
      // the next Tab carries on from where the reader was.
      e.preventDefault();
      close();
      return;
    }
    const at = itemsRef.current.findIndex((el) => el !== null && el === document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusAt(at + 1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusAt(at <= 0 ? order.length - 1 : at - 1);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      focusAt(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      focusAt(order.length - 1);
      return;
    }
    if (/^[1-9]$/.test(e.key)) {
      const target = order[Number(e.key) - 1];
      if (target !== undefined) {
        e.preventDefault();
        choose(target);
      }
    }
  };

  const title =
    disabled && disabledReason !== undefined
      ? disabledReason
      : model === null
        ? `Model: ${currentName}, following the default (the product, then your account)`
        : `Model for this conversation: ${currentName}`;

  const defaultRow: PickerRow = {
    index: 0,
    model: null,
    label: 'Default',
    meta: modelDisplayName(defaultModel),
  };

  const renderRow = (row: PickerRow) => {
    const checked = row.model === model;
    return (
      <ModelRow
        key={row.model ?? 'default'}
        innerRef={(el) => {
          itemsRef.current[row.index] = el;
        }}
        role="menuitemradio"
        checked={checked}
        label={row.label}
        meta={row.meta}
        testId={`${testId}-option-${row.model ?? 'default'}`}
        tabIndex={checked ? 0 : -1}
        onClick={() => choose(row.model)}
      />
    );
  };

  return (
    <div ref={rootRef} className="relative min-w-0 shrink">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        disabled={disabled}
        title={title}
        aria-label={`Model for this conversation: ${currentName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (open) setOpen(false);
          else openList();
        }}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          openList();
        }}
        className={`flex h-7 min-w-0 max-w-full cursor-pointer items-center gap-1.5 rounded-pill border-none px-2 text-[length:var(--text-ui-size)] transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${
          open ? 'bg-inset text-primary' : 'bg-transparent text-secondary'
        }`}
      >
        <ModelGlyph />
        <span className="min-w-0 truncate">{currentName}</span>
      </button>
      {open ? (
        <div
          role="menu"
          // reachable by script, not by Tab: the rows take the focus
          tabIndex={-1}
          aria-label="Model for this conversation"
          data-testid={`${testId}-popover`}
          onKeyDown={onMenuKeyDown}
          data-side={placement.up ? 'up' : 'down'}
          style={{ maxHeight: placement.maxHeight }}
          /* Absolutely placed: opening the list moves nothing under it. */
          className={`absolute left-0 z-50 w-[286px] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-panel border border-default bg-raised p-1.5 shadow-overlay ${
            placement.up ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {renderRow(defaultRow)}
          <TierRows
            model={model}
            testId={testId}
            role="menuitem"
            onPick={(tier) => {
              if (onTier !== undefined) {
                onTier(tier.model, tier.effort);
                close();
              } else {
                choose(tier.model);
              }
            }}
          />
          <AllModelsToggle
            open={listOpen}
            onToggle={() => setShowAll((was) => !was)}
            testId={testId}
          />
          {listOpen
            ? groups.map((group) => (
                <FamilyGroup key={group.key} group={group}>
                  {group.models.map((id) =>
                    renderRow({
                      index: order.indexOf(id),
                      model: id,
                      label: modelDisplayName(id),
                      meta: null,
                    }),
                  )}
                </FamilyGroup>
              ))
            : null}
          <p className="mt-1 mb-0 border-t border-default px-2 pt-2 pb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
            Applies to this conversation. Default follows the product, then your account.
            Costs are bench averages on your key.
          </p>
        </div>
      ) : null}
    </div>
  );
}
